import { useEffect, useCallback, useState } from '@wordpress/element';
import { TextControl, SelectControl, PanelBody } from '@wordpress/components';
import moment from 'moment-timezone';
import counterTimer from '../../../resources/scripts/module/counter';
import {
  parseHubSpotShortcode,
  generateVimeoIframe,
  calculateSecondsToCount,
  getTimezoneDate,
  generateVimeoIframeNonAttributes,
} from '../utils/methods';
import $ from 'jquery';

const { useSelect } = wp.data;
const { registerBlockType } = wp.blocks;
const {
  RichText,
  InspectorControls,
  MediaUpload,
  MediaUploadCheck,
  InnerBlocks,
} = wp.blockEditor;
const { Button } = wp.components;
const { Fragment } = wp.element;
const { __ } = wp.i18n;

const STREAMING_ON_WEBSITE_TYPE = 'streaming_on_website';
const NO_STREAMING_ON_WEBSITE_TYPE = 'no_streaming_on_website';

registerBlockType('pg/webinar-content-post', {
  title: 'PG Webinar Content Block',
  icon: 'calendar',
  category: 'layout',
  attributes: {
    title: { type: 'string' },
    excerpt: { type: 'string' },
    webinarDate: { type: 'string' },
    webinarUpcoming: { type: 'boolean', default: false },
    webinarType: { type: 'string', default: STREAMING_ON_WEBSITE_TYPE },
    webinarFormCode: { type: 'string', default: '' },
    tags: { type: 'string', default: '' },
    titleRegister: { type: 'string' },
    webinarVimeoUrl: { type: 'string', default: '' },
    webinarImage: { type: 'string', default: '' },
    webinarImageAlt: { type: 'string', default: '' },
    typesJson: { type: 'string', default: '[]' },
    types: {
      type: 'array',
      default: [],
      source: 'query',
      selector: '.people-type',
      query: {
        typeName: {
          type: 'string',
          source: 'text',
          selector: '.type-name',
        },
        people: {
          type: 'array',
          default: [],
          source: 'query',
          selector: '.person-card',
          query: {
            id: { type: 'number', source: 'attribute', attribute: 'data-id' },
          },
        },
      },
    },
  },
  edit: ({ attributes, setAttributes }) => {
    const tags = useSelect((select) => {
      const { getEditedPostAttribute } = select('core/editor');
      const tagIds = getEditedPostAttribute('tags');

      if (!tagIds || tagIds.length === 0)
        return 'Add some tags if you need it...';

      return (
        select('core')
          .getEntityRecords('taxonomy', 'post_tag', {
            include: tagIds,
            per_page: -1, // Fetch all tags in one go
          })
          ?.map((tag) => tag.name)
          .join(', ') || 'Loading tags...'
      );
    }, []);

    const metaData = useSelect(
      (select) => select('core/editor').getEditedPostAttribute('meta'),
      []
    );

    const categories = useSelect(
      (select) => select('core/editor').getEditedPostAttribute('categories'),
      []
    );

    const [loaded, setLoaded] = useState(false);
    const [vimeoIframe, setVimeoIframe] = useState('');
    const acfFields = acf.getFields();
    const [types, setTypes] = useState(attributes.types);
    const [changed, setChanged] = useState(false);
    const currentPost = useSelect(
      (select) => select('core/editor').getCurrentPost(),
      null
    );
    const postCreationDate = currentPost && currentPost.date;

    const handleAddType = () => {
      const newType = {
        typeName: '',
        people: [],
      };
      setTypes([...types, newType]);
      setAttributes({
        types: [...types, newType],
        typesJson: JSON.stringify([...types, newType]),
      });
    };

    const updateTypeName = (index, name) => {
      const updatedTypes = [...types];
      updatedTypes[index].typeName = name;
      setTypes(updatedTypes);
      setAttributes({
        types: updatedTypes,
        typesJson: JSON.stringify(updatedTypes),
      });
    };

    const addPersonToType = (typeIndex, selectedIds) => {
      const newPeople = selectedIds
        .filter(
          (id) => !types[typeIndex].people.some((person) => person.id === id)
        )
        .map((id) => ({ id }));
      if (newPeople.length > 0) {
        setTypes((prevTypes) => {
          const updatedTypes = [...prevTypes];
          updatedTypes[typeIndex].people = [
            ...updatedTypes[typeIndex].people,
            ...newPeople,
          ];
          return updatedTypes;
        });

        setAttributes({ ...attributes.types[typeIndex].people, ...newPeople });
        setAttributes({ types: types, typesJson: JSON.stringify(types) });
      }
    };

    const removePersonFromType = (typeIndex, personIndex) => {
      setTypes((prevTypes) => {
        const updatedTypes = [...prevTypes];
        updatedTypes[typeIndex].people.splice(personIndex, 1);
        return updatedTypes;
      });

      const attributesPeople = attributes.types[typeIndex].people;
      attributesPeople.splice(personIndex, 1);
      setAttributes({ [attributes.types[typeIndex].people]: attributesPeople });
      setAttributes({ types: types, typesJson: JSON.stringify(types) });
    };

    const addType = () => {
      setTypes((prevTypes) => [...prevTypes, { typeName: '', people: [] }]);
      handleAddType();
    };

    const removeType = (typeIndex) => {
      setTypes((prevTypes) => {
        let updatedTypes = [...prevTypes];
        updatedTypes.splice(typeIndex, 1);
        // Set attributes here to use the updatedTypes
        setAttributes({
          types: updatedTypes,
          typesJson: JSON.stringify(updatedTypes),
        });
        return updatedTypes;
      });
    };

    // Fetch all people for selection
    const allPeople = useSelect((select) => {
      return select('core').getEntityRecords('postType', 'people', {
        per_page: -1,
      });
    }, []);

    // People options for SelectControl
    const peopleOptions = allPeople
      ? allPeople.map((person) => ({
          label: person.acf?.person_name || person.title.rendered,
          value: person.id,
        }))
      : [];

    const setAttributesData = () => {
      if (metaData.date_of_webinar) {
        let webinarDate = moment(
          metaData.date_of_webinar,
          'YYYYMMDDHHmm'
        ).toDate();

        if (typeof webinarDate === 'object' && isNaN(webinarDate.getTime())) {
          webinarDate = moment(
            metaData.date_of_webinar,
            'DD/MM/YYYY HH:mm'
          ).toDate();
        }

        setAttributes({ webinarDate: webinarDate });
        setAttributes({ webinarUpcoming: webinarDate > new Date() });
      }

      if (metaData.type_of_webinar) {
        setAttributes({ webinarType: metaData.type_of_webinar });
      }

      setAttributes({
        webinarFormCode:
          metaData.article_form_shortcode ||
          metaData.form_shortcode_of_webinar ||
          null,
      });

      if (metaData.webinar_vimeo_url) {
        setAttributes({ webinarVimeoUrl: metaData.webinar_vimeo_url });
      }

      setChanged(!changed);
    };

    const refreshMeta = useCallback(
      (event, field) => {
        event.preventDefault();
        event.stopPropagation();

        if (field.data.type === 'date_picker') {
          metaData[field.data.name] = moment(
            event.target.value,
            'DD/MM/YYYY HH:mm'
          ).format('YYYYMMDDHHmm');
        } else {
          metaData[field.data.name] = event.target.value;
        }

        setAttributesData();
      },
      [metaData, categories]
    );

    useEffect(() => {
      setAttributes({ tags });
    }, [tags]);

    useEffect(() => {
      if (!metaData) {
        return;
      }
      setAttributesData();

      acfFields.forEach((field) =>
        $(`[name=acf\\[${field.data.key}\\]]`).trigger('change')
      );
      acfFields.forEach((field) =>
        $(`[name=acf\\[${field.data.key}\\]]`).on('change', (event) =>
          refreshMeta(event, field)
        )
      );
    }, [metaData, categories]);

    useEffect(() => {
      const refreshAllACFFields = () => {
        acfFields.forEach((field) =>
          $(`[name=acf\\[${field.data.key}\\]]`).trigger('change')
        );
      };

      if (!loaded) {
        refreshAllACFFields();
        setLoaded(true);
      }
    }, [metaData, categories]);

    useEffect(() => {
      if (loaded && attributes.webinarFormCode && window.hbspt) {
        const { portalId, formId } = parseHubSpotShortcode(
          attributes.webinarFormCode
        );

        if (portalId && formId) {
          const formClassTarget = `.my-hubspot-form-${formId}`; // Unique ID based on formId
          window.hbspt.forms.create({
            portalId: portalId,
            formId: formId,
            target: formClassTarget,
          });
        }
      }
    }, [
      window.hbspt,
      changed,
      loaded,
      attributes.webinarFormCode,
      attributes.webinarUpcoming,
    ]);

    useEffect(() => {
      if (attributes.webinarUpcoming) {
        $('.counter, .event-counter').data(
          'timestamp',
          attributes.webinarDate
            ? getTimezoneDate(attributes.webinarDate).unix()
            : 0
        );
        counterTimer(true); // Reset and start the counter
      }
    }, [attributes.webinarDate, attributes.webinarUpcoming]);

    useEffect(() => {
      if (
        attributes.webinarVimeoUrl &&
        attributes.webinarType === STREAMING_ON_WEBSITE_TYPE
      ) {
        const iframeHtml = generateVimeoIframe(attributes.webinarVimeoUrl);
        setVimeoIframe(iframeHtml);
      } else {
        setVimeoIframe('');
      }
    }, [changed, attributes.webinarVimeoUrl, attributes.webinarType]);

    const loadHubSpotScript = () => {
      if (window.hbspt) {
        setLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = '//js.hsforms.net/forms/v2.js';
      script.onload = () => setLoaded(true);
      document.body.appendChild(script);
    };

    useEffect(() => {
      loadHubSpotScript();
    }, []);

    useEffect(() => {
      if (attributes.types && attributes.types.length > 0 && !types.length) {
        setTypes(attributes.types);
      }
    }, [attributes.types]);

    const SelectPeopleControl = ({
      typeIndex,
      peopleOptions,
      selectedPeopleIds,
      onSelectedChange,
    }) => {
      return (
        <SelectControl
          multiple
          label="Select People"
          value={selectedPeopleIds}
          options={peopleOptions}
          onChange={(selectedIds) => {
            onSelectedChange(
              typeIndex,
              selectedIds.map((id) => parseInt(id, 10))
            );
          }}
        />
      );
    };

    const renderVideoBlockContent = () => {
      if (!loaded) {
        return null;
      }

      if (attributes.webinarType === STREAMING_ON_WEBSITE_TYPE && vimeoIframe) {
        return (
          <div
            className="video-block video-block-locked"
            dangerouslySetInnerHTML={{ __html: vimeoIframe }}
          />
        );
      } else if (attributes.webinarType === NO_STREAMING_ON_WEBSITE_TYPE) {
        return (
          <div className="video-block video-block-locked">
            <MediaUploadCheck>
              <MediaUpload
                onSelect={(media) =>
                  setAttributes({
                    webinarImage: media.url,
                    webinarImageAlt: media.alt || '',
                  })
                }
                allowedTypes={['image']}
                value={attributes.webinarImage}
                render={({ open }) => (
                  <>
                    <Button isPrimary onClick={open}>
                      {attributes.webinarImage
                        ? __('Change Image')
                        : __('Upload Image')}
                    </Button>
                    {attributes.webinarImage && (
                      <img
                        src={attributes.webinarImage}
                        alt={attributes.webinarImageAlt ?? ''}
                      />
                    )}
                  </>
                )}
              />
            </MediaUploadCheck>
          </div>
        );
      } else {
        return <div className="video-block video-block-locked"></div>;
      }
    };

    const renderPeople = (types, additionalClass = '', headerClass = '') => {
      return (
        allPeople &&
        types.map((type, typeIndex) => (
          <div className="people-list-block" key={typeIndex}>
            <h3 className={`type-name ${headerClass}`}>
              {type.typeName || `Type ${typeIndex + 1}`}
            </h3>
            <div className={`cards ${additionalClass}`}>
              {type.people.map((person, personIndex) => {
                const personDetails = allPeople
                  ? allPeople.find(
                      (p) => parseInt(p.id) === parseInt(person.id)
                    )
                  : [];
                return (
                  <div
                    className="person-card"
                    key={person.id}
                    data-id={person.id}
                  >
                    <div className="image">
                      <img
                        src={
                          personDetails
                            ? personDetails?.featured_image_url
                            : 'placeholder.jpg'
                        }
                        alt={
                          personDetails
                            ? personDetails?.acf?.person_name
                            : 'Placeholder'
                        }
                      />
                    </div>
                    <div className="data">
                      <p className="name">{personDetails?.acf?.person_name}</p>
                      <p className="position">
                        {personDetails?.acf?.person_title}
                      </p>
                      <p className="company">
                        {personDetails?.acf?.person_company}
                      </p>
                      <Button
                        className={'personRemoveButton'}
                        isDestructive
                        onClick={() =>
                          removePersonFromType(typeIndex, personIndex)
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      );
    };

    return (
      <Fragment>
        <InspectorControls>
          <Button isSecondary onClick={addType}>
            Add Type
          </Button>
          {types.map((type, typeIndex) => (
            <PanelBody
              title={`Type ${typeIndex + 1}`}
              initialOpen={true}
              key={typeIndex}
            >
              <TextControl
                label="Type Name"
                value={type.typeName}
                onChange={(name) => updateTypeName(typeIndex, name)}
              />
              {type.people.map((person, personIndex) => (
                <div key={person.id}>
                  <span>
                    {(allPeople &&
                      allPeople.find((p) => p.id === person.id)?.title
                        .rendered) ||
                      'Unknown'}
                  </span>
                  <Button
                    isDestructive
                    onClick={() => removePersonFromType(typeIndex, personIndex)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <SelectPeopleControl
                typeIndex={typeIndex}
                peopleOptions={peopleOptions}
                selectedPeopleIds={type.people.map((p) => p.id)}
                onSelectedChange={addPersonToType}
              />
              <Button isDestructive onClick={() => removeType(typeIndex)}>
                Remove Type
              </Button>
            </PanelBody>
          ))}
        </InspectorControls>

        <div className="editor-component">
          <section className="section-light-primary">
            <article
              className={`post ${attributes.webinarUpcoming ? 'post-webinar-upcoming' : 'post-webinar'}`}
            >
              <div className={'left'}>
                <header>
                  <div className="meta">
                    <span className="article-tag">{attributes.tags}</span>
                    {loaded && (
                      <time
                        className="dt-published"
                        dateTime={moment(postCreationDate ?? null).format()}
                      >
                        {moment(postCreationDate ?? null).format(
                          'DD MMMM YYYY HH:mm'
                        )}
                      </time>
                    )}
                  </div>

                  <RichText
                    tagName="h1"
                    value={attributes.title}
                    className={'title'}
                    placeholder={__('Title...')}
                    onChange={(value) => setAttributes({ title: value })}
                  />
                  <RichText
                    tagName="div"
                    className={'excerpt'}
                    placeholder={__('Post Excerpt..')}
                    value={attributes.excerpt}
                    onChange={(value) => setAttributes({ excerpt: value })}
                  />
                </header>

                {attributes.webinarUpcoming && (
                  <>
                    <div className="date-block">
                      <div className="date">
                        {moment(attributes.webinarDate)
                          .tz(themeSettings.timezone)
                          .format('DD MMMM YYYY HH:mm')}
                      </div>
                      <div
                        className="counter"
                        data-timestamp={moment(attributes.webinarDate)
                          .tz(themeSettings.timezone)
                          .unix()}
                        data-timezone={themeSettings.timezone}
                      ></div>
                      {renderPeople(
                        types,
                        'cards-columns-2',
                        'type-name--center'
                      )}
                      <div className="form-block">
                        <RichText
                          tagName="h3"
                          value={attributes.titleRegister}
                          placeholder={'Register to watch this webinar'}
                          onChange={(value) =>
                            setAttributes({ titleRegister: value })
                          }
                        />
                        <div
                          className={`my-hubspot-form-${parseHubSpotShortcode(attributes.webinarFormCode).formId || ''}`}
                        ></div>
                      </div>
                    </div>
                  </>
                )}

                <div className="content" data-people={attributes.typesJson}>
                  {renderVideoBlockContent()}

                  <InnerBlocks
                    allowedBlocks={[
                      'core/paragraph',
                      'core/image',
                      'core/heading',
                      'core/list',
                      'core/embed',
                      'core/table',
                      'core/freeform',
                      'core/quote',
                      'pg/quote',
                      'pg/button',
                      'pg/banner',
                      'pg/list',
                    ]}
                    template={[
                      [
                        'core/paragraph',
                        {
                          placeholder:
                            'Start writing or type / to choose a block',
                        },
                      ],
                      ['core/image'],
                    ]}
                  />
                  {!attributes.webinarUpcoming ? renderPeople(types) : ''}
                </div>
              </div>

              {!attributes.webinarUpcoming && (
                <div className="right">
                  <div className="green-box">
                    <RichText
                      tagName="h4"
                      value={attributes.titleRegister}
                      placeholder={'Register to watch this webinar'}
                      onChange={(value) =>
                        setAttributes({ titleRegister: value })
                      }
                    />
                    <div
                      className={`my-hubspot-form-${parseHubSpotShortcode(attributes.webinarFormCode).formId || ''}`}
                    ></div>
                  </div>
                </div>
              )}
            </article>
          </section>
        </div>
      </Fragment>
    );
  },
  save: ({ attributes }) => {
    return (
      <section className="section-light-primary">
        <article
          className={`post ${attributes.webinarUpcoming ? 'post-webinar-upcoming' : 'post-webinar'}`}
        >
          <div className="left">
            <header>
              <div className="meta">
                <span className="article-tag">{attributes.tags}</span>
              </div>
              <h2 className="title">{attributes.title}</h2>
              <div className="excerpt">{attributes.excerpt}</div>
            </header>
            {attributes.webinarUpcoming && (
              <>
                <div className="date-block">
                  <div className="date">
                    {moment(attributes.webinarDate).format(
                      'DD MMMM YYYY HH:mm'
                    )}
                  </div>
                  <div className="form-block">
                    <h3>{attributes.titleRegister}</h3>
                    <div
                      className={`my-hubspot-form-${parseHubSpotShortcode(attributes.webinarFormCode).formId || ''}`}
                    ></div>
                  </div>
                </div>
                {attributes.types.map((type, typeIndex) => (
                  <div className="people-type" key={typeIndex}>
                    <h3 className="type-name type-name--center">
                      {type.typeName || `Type ${typeIndex + 1}`}
                    </h3>
                    <div className="cards cards-columns-2">
                      {type.people.map((person) => (
                        <div
                          className="person-card"
                          key={person.id}
                          data-id={person.id}
                        ></div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
            <div className="content" data-people={attributes.typesJson}>
              <div className="video-block">
                <div
                  dangerouslySetInnerHTML={{
                    __html: generateVimeoIframeNonAttributes(
                      attributes.webinarVimeoUrl
                    ),
                  }}
                ></div>
              </div>
              <InnerBlocks.Content />
              {!attributes.webinarUpcoming &&
                attributes.types.map((type, typeIndex) => (
                  <div className="people-type" key={typeIndex}>
                    <h3 className="type-name">
                      {type.typeName || `Type ${typeIndex + 1}`}
                    </h3>
                    <div className="cards">
                      {type.people.map((person) => (
                        <div
                          className="person-card"
                          key={person.id}
                          data-id={person.id}
                        ></div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </div>
          {!attributes.webinarUpcoming && (
            <div className="right">
              <div className="green-box">
                <h4>{attributes.titleRegister}</h4>
                <div
                  className={`my-hubspot-form-${parseHubSpotShortcode(attributes.webinarFormCode).formId || ''}`}
                ></div>
              </div>
            </div>
          )}
        </article>
      </section>
    );
  },
});
